import Big, { RoundingMode } from "big.js";
import { UseFormReturn } from "react-hook-form";
import {
  jodaihyojiKbn,
  kinmarumeKbn,
  tankamarumeKbn,
  zeiKbn,
  zeimarumeKbn,
} from "./codes";

// 受注入力(フロント詳細設計書)の構成品の上代単価算出処理、処理詳細7、処理詳細8で使用する
/**
 * 上代単価算出メソッド
 *
 * @param methods methods(ヘッダー)
 * @param jodaitanka 計算前の上代単価
 * @returns 上代単価
 */
export const getJodaitanka = (
  methods: UseFormReturn,
  jodaitanka: string
): string | "" => {
  const fields = methods.getValues();
  // 上代単価が空の場合、何もしない
  if (!jodaitanka) {
    return jodaitanka;
  }
  let kingaku;
  const zeiRitsu = fields.zeiRitsu;
  const zeimarumeKbn = fields.zeimarumeKbn;
  //受注.zeiKbn(消費税区分)が「0」(外税/明細単位)かつ 受注.jodaihyojiKbn(上代表示区分)が「1」(内税)の場合
  if (
    fields.zeiKbn === zeiKbn.sotozeiMei &&
    fields.jodaihyojiKbn === jodaihyojiKbn.uchizei
  ) {
    kingaku = getZeikomiKin(jodaitanka, zeiRitsu, zeimarumeKbn);
  }
  //受注.zeiKbn(消費税区分)が「1」(外税/伝票単位)かつ 受注.jodaihyojiKbn(上代表示区分)が「1」(内税)の場合
  else if (
    fields.zeiKbn === zeiKbn.sotozeiDen &&
    fields.jodaihyojiKbn === jodaihyojiKbn.uchizei
  ) {
    kingaku = getZeikomiKin(jodaitanka, zeiRitsu, zeimarumeKbn);
  }
  //受注.zeiKbn(消費税区分)が「2」(外税/請求単位)かつ 受注.jodaihyojiKbn(上代表示区分)が「1」(内税)の場合
  else if (
    fields.zeiKbn === zeiKbn.sotozeiSeikyu &&
    fields.jodaihyojiKbn === jodaihyojiKbn.uchizei
  ) {
    kingaku = getZeikomiKin(jodaitanka, zeiRitsu, zeimarumeKbn);
  }
  //受注.zeiKbn(消費税区分)が「3」(内税/明細単位)かつ 受注.jodaihyojiKbn(上代表示区分)が「0」(通常)または「1」(内税)の場合
  else if (
    fields.zeiKbn === zeiKbn.uchizeiMei &&
    (fields.jodaihyojiKbn === jodaihyojiKbn.tsujyo ||
      fields.jodaihyojiKbn === jodaihyojiKbn.uchizei)
  ) {
    kingaku = getZeikomiKin(jodaitanka, zeiRitsu, zeimarumeKbn);
  }
  //受注.zeiKbn(消費税区分)が「4」(非課税)かつ 受注.jodaihyojiKbn(上代表示区分)が「1」(内税)の場合
  else if (
    fields.zeiKbn === zeiKbn.hikazei &&
    fields.jodaihyojiKbn === jodaihyojiKbn.uchizei
  ) {
    kingaku = getZeikomiKin(jodaitanka, zeiRitsu, zeimarumeKbn);
  }
  //それ以外の場合
  else {
    kingaku = jodaitanka;
  }

  return kingaku;
};

// 受注入力(フロント詳細設計書)の処理詳細7と処理詳細9で使用する
/**
 *「返却値(上代単価) × (100 + 受注.zeiRitsu(消費税率))/100」のメソッド
 *
 * @param kingaku 金額
 * @param zeiritsu 消費税率
 * @param marumeKbn 消費税丸め区分
 * @returns 税込金額
 */
const getZeikomiKin = (
  kingaku: string,
  zeiritsu: string,
  marumeKbn: number
): string => {
  const jodaitanka = new Big(kingaku);
  const zei = new Big(100 + zeiritsu);

  //「返却値(上代単価) × (100 + 受注.zeiRitsu(消費税率))/100」の計算結果
  let result = jodaitanka.times(zei).div(100);

  //消費税丸め区分が「0(四捨五入)」の場合
  if (marumeKbn === zeimarumeKbn.shishagonyu) {
    //resultを四捨五入
    result = result.round(0);
  }
  //消費税丸め区分が「1(切り捨て)」の場合
  else if (marumeKbn === zeimarumeKbn.kirisute) {
    //resultを切り捨て
    result = result.round(0, Big.roundDown);
  }
  //消費税丸め区分が「2(切り上げ)」の場合
  else if (marumeKbn === zeimarumeKbn.kiriage) {
    //resultを切り上げ
    result = result.round(0, Big.roundUp);
  }

  return result.toString();
};

// 受注入力(フロント詳細設計書)の構成品の上代単価算出処理、処理詳細7、処理詳細8で使用する
/**
 * 下代単価算出メソッド
 *
 * @param methods methods(ヘッダー)
 * @param jodaitanka 上代単価
 * @param kakeritsu 掛率
 * @returns 下代単価(受注単価)
 */
export const getJuchutanka = (
  methods: UseFormReturn,
  jodaitanka: string,
  kakeritsu: string
): string => {
  // フォームのデータを取得する。
  const fields = methods.getValues();

  // 単価丸め区分を取得
  let tankaRoundingMode: RoundingMode;
  // 四捨五入
  if (fields.tankamarumeKbn === tankamarumeKbn.round) {
    tankaRoundingMode = Big.roundHalfUp;
  }
  //切り捨て
  else if (fields.tankamarumeKbn === tankamarumeKbn.floor) {
    tankaRoundingMode = Big.roundDown;
  }
  //切り上げ
  else if (fields.tankamarumeKbn === tankamarumeKbn.ceil) {
    tankaRoundingMode = Big.roundUp;
  }

  // 単価端数位置を取得
  // Big.jsの端数位置と、得意先マスタ.単価端数位置は+-が逆転している
  // 使用する際は、反転させること
  const tankahasuichi = fields.tankahasuichi;

  // 下代単価
  let juchutanka: Big = new Big(0);
  // 消費税区分が「2」(外税/請求単位)かつ 上代表示区分が「1」(内税)の場合
  if (
    fields.zeiKbn === zeiKbn.sotozeiSeikyu &&
    fields.jodaihyojiKbn === jodaihyojiKbn.uchizei
  ) {
    // 上代単価 * 掛率 / (100 + 消費税) ※得意先ごとの丸め
    juchutanka = new Big(jodaitanka ? jodaitanka : "0")
      .mul(new Big(kakeritsu ? kakeritsu : "0"))
      .div(new Big("100").add(new Big(fields.zeiRitsu!)))
      // マイナスを付与する
      .round(-Number(tankahasuichi), tankaRoundingMode!);
  }
  // 上記以外
  else {
    // 上代単価 × 掛け率 / 100 ※得意先ごとの丸め
    juchutanka = new Big(jodaitanka ? jodaitanka : "0")
      .mul(new Big(kakeritsu ? kakeritsu : "0"))
      .div(new Big("100"))
      // マイナスを付与する
      .round(-Number(tankahasuichi), tankaRoundingMode!);
  }

  return juchutanka.toString();
};

/**
 * 内税込みの下代単価算出メソッド(下代単価 × (100 + 消費税率) / 100で取得)
 *
 * @param methods methods(ヘッダー)
 * @param gedaitanka 上代単価
 * @returns 下代単価(内税込み)
 */
export const getUchizeiJuchutanka = (
  methods: UseFormReturn,
  gedaitanka: string
): string => {
  const fields = methods.getValues();
  // 上代単価が空の場合、何もしない
  if (!gedaitanka) {
    return gedaitanka;
  }
  let kingaku;
  const zeiRitsu = fields.zeiRitsu;
  const zeimarumeKbn = fields.zeimarumeKbn;
  const tankahasuichi = fields.tankahasuichi;

  // 消費税区分が「2」(外税/請求単位)かつ 上代表示区分が「1」(内税)の場合
  if (
    fields.zeiKbn === zeiKbn.sotozeiSeikyu &&
    fields.jodaihyojiKbn === jodaihyojiKbn.uchizei
  ) {
    kingaku = getZeikomiGedaiKin(
      gedaitanka,
      zeiRitsu,
      zeimarumeKbn,
      tankahasuichi
    );
  }
  //消費税区分が「3」(内税/明細単位)かつ 上代表示区分が「0」(通常)または「1」(内税)の場合
  else if (
    fields.zeiKbn === zeiKbn.uchizeiMei &&
    (fields.jodaihyojiKbn === jodaihyojiKbn.tsujyo ||
      fields.jodaihyojiKbn === jodaihyojiKbn.uchizei)
  ) {
    kingaku = getZeikomiGedaiKin(
      gedaitanka,
      zeiRitsu,
      zeimarumeKbn,
      tankahasuichi
    );
  }
  //それ以外の場合
  else {
    kingaku = gedaitanka;
  }

  return kingaku;
};

/**
 * 下代単価取得「返却値(単価) × (100 + 消費税率) / 100」のメソッド(単価端数位置を考慮)
 *
 * @param kingaku 金額
 * @param zeiritsu 消費税率
 * @param marumeKbn 消費税丸め区分
 * @param tankahasuichi 単価端数位置(Big.jsの端数位置と、得意先マスタ.単価端数位置は+-が逆転しているため反転させること)
 * @returns 税込金額
 */
const getZeikomiGedaiKin = (
  kingaku: string,
  zeiritsu: string,
  marumeKbn: number,
  tankahasuichi: number
): string => {
  const tanka = new Big(kingaku);
  const zei = new Big(100 + zeiritsu);

  //「返却値(単価) × (100 + 消費税率) / 100」の計算結果
  let result = tanka.times(zei).div(100);

  //消費税丸め区分が「0(四捨五入)」の場合
  if (marumeKbn === zeimarumeKbn.shishagonyu) {
    //resultを四捨五入
    //単価端数位置にマイナスを付与する
    result = result.round(-Number(tankahasuichi), Big.roundHalfUp);
  }
  //消費税丸め区分が「1(切り捨て)」の場合
  else if (marumeKbn === zeimarumeKbn.kirisute) {
    //resultを切り捨て
    //単価端数位置にマイナスを付与する
    result = result.round(-Number(tankahasuichi), Big.roundDown);
  }
  //消費税丸め区分が「2(切り上げ)」の場合
  else if (marumeKbn === zeimarumeKbn.kiriage) {
    //resultを切り上げ
    //単価端数位置にマイナスを付与する
    result = result.round(-Number(tankahasuichi), Big.roundUp);
  }

  return result.toString();
};

// 受注入力(フロント詳細設計書)の処理詳細6で使用する
/**
 * 掛率算出メソッド
 *
 * @param juchutanka 受注単価
 * @param jodaitanka 上代単価
 * @returns 掛率
 */
export const getKakeritsu = (
  juchutanka: string,
  jodaitanka: string
): string => {
  // 上代単価が未設定OR「0」の場合、掛率は「0」にする
  if (!jodaitanka || jodaitanka === "0") {
    return "0";
  }

  // 掛率(受注単価 * 100 / 上代単価) ※小数点以下切り捨て
  const kakeritsu: Big = new Big(juchutanka ? juchutanka : "0")
    .times(new Big("100"))
    .div(new Big(jodaitanka))
    .round(0, Big.roundDown);

  return kakeritsu.toString();
};

/**
 * 受注入力(フロント詳細設計書)の処理詳細7、処理詳細8で使用する
   受注金額算出メソッド
 * @param juchutanka 下代単価
 * @param juchusu 受注数(必要数)
 * @param kinmarumeKbnData 金額丸め区分
 * @returns 受注金額
 */
export const getJuchuKin = (
  juchutanka: string,
  juchusu: string,
  kinmarumeKbnData: number
): string => {
  // 金額丸め区分を取得
  let kingakuRoundingMode: RoundingMode;
  // 四捨五入
  if (kinmarumeKbnData === kinmarumeKbn.round) {
    kingakuRoundingMode = Big.roundHalfUp;
  }
  //切り捨て
  else if (kinmarumeKbnData === kinmarumeKbn.floor) {
    kingakuRoundingMode = Big.roundDown;
  }
  //切り上げ
  else if (kinmarumeKbnData === kinmarumeKbn.ceil) {
    kingakuRoundingMode = Big.roundUp;
  }

  // 受注金額(受注単価 * 受注数) ※小数点以下は許容しない
  const juchuKin: Big = new Big(juchutanka ? juchutanka : "0")
    .mul(new Big(juchusu ? juchusu : "0"))
    .round(0, kingakuRoundingMode!);

  return juchuKin.toString();
};

/**
 * 必要数算出メソッド
 *
 * @param koseisu 構成数
 * @param juchusu 受注数
 * @returns 必要数
 */
export const getHitsuyosu = (koseisu: string, juchusu: string): string => {
  // 必要数(構成数 * 受注数)
  const hitsuyosu: Big = new Big(koseisu ? koseisu : "0").mul(
    new Big(juchusu ? juchusu : "0")
  );

  return hitsuyosu.toString();
};

export const digit = (value: string | number | undefined): string => {
  if (typeof value === "undefined") return "";
  if (value === null) return "";
  if (value === "") return "";
  return Number(value).toLocaleString();
};
